import { FormControl, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import {
  FormikSelectField,
  type FormikSelectOption,
  FormikTextField,
} from '@frontend/components';
import { FormikSelectMultipleField } from '@frontend/components/formik-select-multiple-field.component';
import { tCommon, tOrder } from '@frontend/locales';
import { ALL_PLACES_ID, useShopContext } from '@frontend/utils';

import type {
  OrderFilterInput,
  OrderFilterInputStatusType,
  PeriodModel,
} from './order-filter-input.interface';

export const OrderFilterContent = () => {
  const {
    shop: { places },
  } = useShopContext();

  const { setFieldValue } = useFormikContext<OrderFilterInput>();

  const statusOptions = useMemo<
    FormikSelectOption<OrderFilterInputStatusType>[]
  >(() => {
    return [
      { id: 'ALL', displayName: tCommon.actions.allStatuses() },
      { id: 'PENDING', displayName: tOrder.orderStatus.pending() },
      { id: 'CONFIRMED', displayName: tOrder.orderStatus.confirmed() },
      { id: 'READY', displayName: tOrder.orderStatus.ready() },
      { id: 'CLOSED', displayName: tOrder.orderStatus.closed() },
      { id: 'REFUNDED', displayName: tOrder.orderStatus.refunded() },
      { id: 'REJECTED', displayName: tOrder.orderStatus.rejected() },
    ];
  }, []);

  const periodOptions = useMemo<FormikSelectOption<PeriodModel>[]>(() => {
    return [
      { id: 'TODAY', displayName: tOrder.periodOptions.TODAY() },
      { id: 'THIS_WEEK', displayName: tOrder.periodOptions.THIS_WEEK() },
      { id: 'THIS_MONTH', displayName: tOrder.periodOptions.THIS_MONTH() },
    ];
  }, []);

  const placeOptions = useMemo(() => {
    const realPlaces =
      places
        ?.map((place) => ({
          id: place.id,
          displayName: place.name,
        }))
        ?.sort((a, b) => a.displayName.localeCompare(b.displayName)) ?? [];

    return [
      { id: ALL_PLACES_ID, displayName: tCommon.actions.allPlaces() },
      ...realPlaces,
    ];
  }, [places]);

  const onStatusSelectionChange = (selected: OrderFilterInputStatusType[]) => {
    const lastIndex = selected.length - 1;
    const isNonSelected = lastIndex === -1;
    const isSelectAllSelected = selected[lastIndex] === 'ALL';
    const isAllSelected =
      selected.length === statusOptions.length - 1 && !selected.includes('ALL');

    if (isNonSelected || isAllSelected || isSelectAllSelected) {
      setFieldValue('status', ['ALL']);
    } else {
      setFieldValue(
        'status',
        selected.filter((s) => s !== 'ALL'),
      );
    }
  };

  const onPlaceSelectionChange = (selected: string[]) => {
    const lastIndex = selected.length - 1;
    const isNonSelected = lastIndex === -1;
    const isSelectAllSelected = selected[lastIndex] === ALL_PLACES_ID;
    const isAllSelected =
      selected.length === placeOptions.length - 1 &&
      !selected.includes(ALL_PLACES_ID);

    if (isNonSelected || isAllSelected || isSelectAllSelected) {
      setFieldValue('placeIds', [ALL_PLACES_ID]);
    } else {
      setFieldValue(
        'placeIds',
        selected.filter((id) => id !== ALL_PLACES_ID),
      );
    }
  };

  return (
    <FormControl variant="outlined" fullWidth>
      <Stack py={2} spacing={2}>
        <FormikSelectMultipleField
          name="placeIds"
          label={tCommon.headers.places()}
          options={placeOptions}
          disabled={!places || places.length === 0}
          helperText={
            !places || places.length === 0
              ? tCommon.descriptions.shopHasNoPlaces()
              : undefined
          }
          onChange={(value) => {
            onPlaceSelectionChange(value);
          }}
        />
        <FormikSelectField
          name="period"
          label={tOrder.headers.period()}
          options={periodOptions}
          required
        />
        <FormikSelectMultipleField
          name="status"
          label={tCommon.general.status()}
          options={statusOptions}
          required
          onChange={(value) => {
            onStatusSelectionChange(value as OrderFilterInputStatusType[]);
          }}
        />
        <FormikTextField
          name="preOrder"
          label={tCommon.headers.preOrders()}
          type="number"
          helperText={tCommon.descriptions.preOrders()}
        />
      </Stack>
    </FormControl>
  );
};
